import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { baseUrl } from "../utils/utils";

// ✅ 1. Fetch Receipt List
export const get_receipt_list = createAsyncThunk(
  "receipt/getList",
  async ({ page, limit }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("action", "list");
      formData.append("page", page);
      formData.append("limit", limit);

      const response = await fetch(baseUrl + "receipt.php", {
        method: "POST",
        body: formData,
      });

      const data = await response.json();
      if (!data.success) {
        return rejectWithValue(data.message);
      }

      return data;
    } catch (error) {
      return rejectWithValue("Failed to fetch receipts");
    }
  }
);

// ✅ 2. Add New Receipt (Final Clean Version)
export const add_receipt = createAsyncThunk(
  "receipt/add",
  async (payload, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("action", "add");
      formData.append("userid", payload.userid);
      formData.append("voucher_date", payload.voucher_date);

      // ✅ Send only 'amount'
      formData.append("amount", payload.amount);

      formData.append("payment_mode", payload.payment_mode);
      formData.append("transaction_number", payload.transaction_number || "");
      formData.append("mode_of_transaction", payload.mode_of_transaction || "");
      formData.append("narration", payload.narration || ""); // optional

      // ✅ Ensure orderid always sent as array
      const orderIds = Array.isArray(payload.orderid)
        ? payload.orderid
        : [payload.orderid];
      orderIds.forEach((id, index) => {
        formData.append(`orderid[${index}]`, id);
      });

      formData.append(
        "bank_id",
        payload.payment_mode === "2" ? payload.bank_id : ""
      );

      const response = await fetch(baseUrl + "receipt.php", {
        method: "POST",
        body: formData,
      });

      const data = await response.json();
      if (!data.success) {
        return rejectWithValue(data.message);
      }

      return data;
    } catch (error) {
      return rejectWithValue("Failed to add receipt");
    }
  }
);




// ✅ 3. Delete Receipt
export const delete_receipt = createAsyncThunk(
  "receipt/delete",
  async (id, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("action", "delete");
      formData.append("id", id);

      const response = await fetch(baseUrl + "receipt.php", {
        method: "POST",
        body: formData,
      });

      const data = await response.json();
      if (!data.success) {
        return rejectWithValue(data.message);
      }

      return data;
    } catch (error) {
      return rejectWithValue("Failed to delete receipt");
    }
  }
);

// ✅ 4. Approve / Update Payment
export const update_payment = createAsyncThunk(
  "receipt/updatePayment",
  async ({ id, userid, amount, payment_mode, orderid }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("action", "update_payment");
      formData.append("id", id);
      formData.append("userid", userid);
      formData.append("amount", amount);
      formData.append("payment_mode", payment_mode);
      formData.append("status", 1);

      // If you have multiple order IDs, send them comma-separated
      if (Array.isArray(orderid)) {
        formData.append("orderid", orderid.join(","));
      } else {
        formData.append("orderid", orderid);
      }

      const response = await fetch(baseUrl + "receipt.php", {
        method: "POST",
        body: formData, // ✅ send as FormData, no Content-Type header
      });

      const data = await response.json();

      if (!data.success) {
        return rejectWithValue(data.message || "Failed to update payment");
      }

      return data;
    } catch (error) {
      return rejectWithValue("Failed to update payment");
    }
  }
);


const initialState = {
  receipt_list: [],
  total_receipt_count: 0,
  loading: false,
  action_loading: false,
  error: null,
};

const receiptSlice = createSlice({
  name: "receipt",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // ✅ Get List
      .addCase(get_receipt_list.pending, (state) => {
        state.loading = true;
      })
      .addCase(get_receipt_list.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload;
        state.receipt_list = data.data || [];
        state.total_receipt_count = parseInt(data.totalcount) || 0;
      })
      .addCase(get_receipt_list.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ✅ Add Receipt
      .addCase(add_receipt.pending, (state) => {
        state.action_loading = true;
      })
      .addCase(add_receipt.fulfilled, (state) => {
        state.action_loading = false;
      })
      .addCase(add_receipt.rejected, (state, action) => {
        state.action_loading = false;
        state.error = action.payload;
      })

      // ✅ Delete Receipt
      .addCase(delete_receipt.pending, (state) => {
        state.action_loading = true;
      })
      .addCase(delete_receipt.fulfilled, (state) => {
        state.action_loading = false;
      })
      .addCase(delete_receipt.rejected, (state, action) => {
        state.action_loading = false;
        state.error = action.payload;
      })

      // ✅ Update Payment
      .addCase(update_payment.pending, (state) => {
        state.action_loading = true;
      })
      .addCase(update_payment.fulfilled, (state) => {
        state.action_loading = false;
      })
      .addCase(update_payment.rejected, (state, action) => {
        state.action_loading = false;
        state.error = action.payload;
      });
  },
});

export default receiptSlice.reducer;
